/**
 * Image compression and validation utilities for upload optimization.
 * Uses Canvas API to resize and compress images client-side before S3 upload.
 */

export const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB - reject if still over after compression
export const TARGET_FILE_SIZE = 1 * 1024 * 1024; // 1MB - target size after compression
export const MAX_DIMENSION = 1920; // Max width/height in pixels
export const COMPRESSION_QUALITY = 0.8; // JPEG quality (0-1)
/** Must stay ≤ server S3 POST content-length-range max (10MB). */
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

/** Minimum shortest edge (px) for vehicle listing photos — smaller images are rejected */
export const MIN_LISTING_IMAGE_SHORT_EDGE = 400;
/** Minimum shortest edge (px) for profile photos */
export const MIN_PROFILE_IMAGE_SHORT_EDGE = 128;

const IMAGE_EXT_RE = /\.(jpe?g|png|webp|gif|bmp|heic|heif)$/i;

export interface ValidationResult {
  valid: boolean;
  error?: string;
}

/** True when the browser MIME or filename indicates a raster image. */
export function looksLikeImageFile(file: File): boolean {
  if (file.type && file.type.startsWith("image/")) return true;
  // iOS/Safari often hands File objects with an empty `type` for camera roll
  // JPEGs. Fall back to the extension so we still compress / validate them.
  return IMAGE_EXT_RE.test(file.name || "");
}

/**
 * Validate an image file for type and basic checks.
 */
export function validateImageFile(file: File): ValidationResult {
  if (!looksLikeImageFile(file)) {
    return { valid: false, error: `"${file.name}" is not an image file` };
  }

  // We'll compress large files, but warn if extremely large (over 20MB raw)
  if (file.size > 20 * 1024 * 1024) {
    return { valid: false, error: `"${file.name}" is too large (over 20MB). Please use a smaller image.` };
  }

  return { valid: true };
}

/**
 * Full validation including decoding the image and checking minimum resolution.
 * Use this on file selection for uploads; `validateImageFile` only checks type and raw size.
 */
export async function validateImageFileAsync(
  file: File,
  options: { minShortEdge: number }
): Promise<ValidationResult> {
  const preliminary = validateImageFile(file);
  if (!preliminary.valid) {
    return preliminary;
  }

  try {
    const img = await loadImage(file);
    const shortEdge = Math.min(img.naturalWidth, img.naturalHeight);
    if (shortEdge < options.minShortEdge) {
      return {
        valid: false,
        error: `"${file.name}" is too small (${img.naturalWidth}×${img.naturalHeight}px). Photos must be at least ${options.minShortEdge}px on the shortest side.`,
      };
    }
    return { valid: true };
  } catch {
    return {
      valid: false,
      error: `"${file.name}" could not be read as an image.`,
    };
  }
}

/**
 * Load a File into an HTMLImageElement.
 */
function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(img.src);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error(`Failed to load image: ${file.name}`));
    };
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Calculate new dimensions maintaining aspect ratio, capped at maxDimension.
 */
function calculateDimensions(
  width: number,
  height: number,
  maxDimension: number
): { width: number; height: number; needsResize: boolean } {
  const longestEdge = Math.max(width, height);
  
  if (longestEdge <= maxDimension) {
    return { width, height, needsResize: false };
  }

  const scale = maxDimension / longestEdge;
  return {
    width: Math.round(width * scale),
    height: Math.round(height * scale),
    needsResize: true,
  };
}

function asJpegFile(blob: Blob, originalName: string): File {
  const baseName = originalName.replace(/\.[^.]+$/, "") || "photo";
  return new File([blob], `${baseName}.jpg`, { type: "image/jpeg" });
}

/**
 * Compress and optionally resize an image file.
 * Returns the original file if it's already small enough and doesn't need resizing,
 * but only when it still fits under the S3 upload ceiling.
 */
export async function compressImage(
  file: File,
  options: {
    maxDimension?: number;
    quality?: number;
    targetSize?: number;
    maxUploadBytes?: number;
  } = {}
): Promise<File> {
  const {
    maxDimension = MAX_DIMENSION,
    quality = COMPRESSION_QUALITY,
    targetSize = TARGET_FILE_SIZE,
    maxUploadBytes = MAX_UPLOAD_BYTES,
  } = options;

  // Skip non-image files (e.g. optional title PDFs).
  if (!looksLikeImageFile(file)) {
    return file;
  }

  // Load the image
  const img = await loadImage(file);
  const { width, height, needsResize } = calculateDimensions(
    img.naturalWidth,
    img.naturalHeight,
    maxDimension
  );

  // Already small + typed image: keep original (no re-encode).
  // Empty MIME (common on iOS) falls through to canvas so we emit a real
  // image/jpeg with a matching Content-Type for the S3 POST policy.
  if (
    !needsResize &&
    file.size <= targetSize &&
    file.size <= maxUploadBytes &&
    file.type &&
    file.type.startsWith("image/")
  ) {
    if (file.type === "image/jpg") {
      return new File([file], file.name.replace(/\.jpe?g$/i, ".jpg") || `${file.name}.jpg`, {
        type: "image/jpeg",
      });
    }
    return file;
  }

  // Create canvas and draw resized image
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    if (file.size > maxUploadBytes) {
      throw new Error(
        `"${file.name}" is too large to upload and could not be compressed in this browser. Please choose a smaller photo.`
      );
    }
    console.warn("Canvas context not available, returning original file");
    return file;
  }

  // Use high-quality image smoothing
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, 0, 0, width, height);

  // Convert to blob with compression; retry at lower quality if still huge.
  let blob: Blob | null = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, "image/jpeg", quality);
  });

  if (blob && blob.size > maxUploadBytes) {
    blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, "image/jpeg", 0.6);
    });
  }
  if (blob && blob.size > maxUploadBytes) {
    blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, "image/jpeg", 0.4);
    });
  }

  if (!blob) {
    if (file.size > maxUploadBytes) {
      throw new Error(
        `"${file.name}" could not be compressed for upload. Please choose a smaller photo.`
      );
    }
    console.warn("Failed to compress image, returning original file");
    return file;
  }

  if (blob.size > maxUploadBytes) {
    throw new Error(
      `"${file.name}" is still too large after compression (${formatFileSize(blob.size)}). Photos must be under ${formatFileSize(maxUploadBytes)}.`
    );
  }

  // If compressed version is larger than original (rare) and original fits, keep original.
  if (blob.size >= file.size && !needsResize && file.size <= maxUploadBytes) {
    if (!file.type || file.type === "image/jpg") {
      return new File([file], file.name.replace(/\.jpe?g$/i, ".jpg") || `${file.name}.jpg`, {
        type: "image/jpeg",
      });
    }
    return file;
  }

  return asJpegFile(blob, file.name);
}

/**
 * Compress multiple images in parallel.
 */
export async function compressImages(
  files: File[],
  options?: Parameters<typeof compressImage>[1]
): Promise<File[]> {
  return Promise.all(files.map((file) => compressImage(file, options)));
}

/**
 * Format file size for display.
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
